import { z } from "zod";
import { monotonicFactory } from "ulid";

/** 单调 ULID：同一毫秒内生成的 ID 也严格递增（按 id 排序 = 按创建顺序） */
const ulid = monotonicFactory();

/** 所有实体 ID 的前缀。ID = `<prefix>_<ULID>`，见 doc/api_v1.md §0。 */
export const ID_PREFIXES = {
  tenant: "ten",
  user: "usr",
  project: "prj",
  agent: "agt",
  template: "tpl",
  credential: "crd",
  conversation: "cnv",
  message: "msg",
  task: "tsk",
  meeting: "mtg",
  asset: "ast",
  approval: "apr",
  node: "nod",
  session: "ses",
  terminal: "trm",
  wake: "wak",
  request: "req",
  device: "dev",
  notification: "ntf",
  repo: "rpo",
  listing: "lst",
  rental: "rnt",
  mediaJob: "job",
  secret: "sec",
  mcpServer: "mcs",
  /** ask_human 的问题（bus_questions） */
  question: "qst",
} as const;

export type IdKind = keyof typeof ID_PREFIXES;

const ULID_RE = "[0-9A-HJKMNP-TV-Z]{26}";

export function idSchema<K extends IdKind>(kind: K) {
  const prefix = ID_PREFIXES[kind];
  return z
    .string()
    .regex(new RegExp(`^${prefix}_${ULID_RE}$`), `invalid ${kind} id`)
    .brand<`${K}Id`>();
}

export function newId<K extends IdKind>(kind: K): z.infer<ReturnType<typeof idSchema<K>>> {
  return `${ID_PREFIXES[kind]}_${ulid()}` as z.infer<ReturnType<typeof idSchema<K>>>;
}

export const TenantId = idSchema("tenant");
export type TenantId = z.infer<typeof TenantId>;
export const UserId = idSchema("user");
export type UserId = z.infer<typeof UserId>;
export const ProjectId = idSchema("project");
export type ProjectId = z.infer<typeof ProjectId>;
export const AgentId = idSchema("agent");
export type AgentId = z.infer<typeof AgentId>;
export const TemplateId = idSchema("template");
export type TemplateId = z.infer<typeof TemplateId>;
export const CredentialId = idSchema("credential");
export type CredentialId = z.infer<typeof CredentialId>;
export const ConversationId = idSchema("conversation");
export type ConversationId = z.infer<typeof ConversationId>;
export const MessageId = idSchema("message");
export type MessageId = z.infer<typeof MessageId>;
export const TaskId = idSchema("task");
export type TaskId = z.infer<typeof TaskId>;
export const ApprovalId = idSchema("approval");
export type ApprovalId = z.infer<typeof ApprovalId>;
export const NodeId = idSchema("node");
export type NodeId = z.infer<typeof NodeId>;
export const SessionId = idSchema("session");
export type SessionId = z.infer<typeof SessionId>;
export const TerminalId = idSchema("terminal");
export type TerminalId = z.infer<typeof TerminalId>;
export const WakeId = idSchema("wake");
export type WakeId = z.infer<typeof WakeId>;
export const QuestionId = idSchema("question");
export type QuestionId = z.infer<typeof QuestionId>;

/** ISO-8601 UTC 时间戳。 */
export const Timestamp = z.iso.datetime();
export type Timestamp = z.infer<typeof Timestamp>;

/** 首批支持 zh-CN / en；新增语言只需扩展这里并补翻译目录。 */
export const SUPPORTED_LOCALES = ["zh-CN", "en"] as const;
export const Locale = z.enum(SUPPORTED_LOCALES);
export type Locale = z.infer<typeof Locale>;
export const DEFAULT_LOCALE: Locale = "en";

/** 多语言字段：读取时按 locale → en → 任意一个 回退。 */
export const LocalizedText = z.partialRecord(Locale, z.string()).refine((v) => Object.keys(v).length > 0, {
  message: "LocalizedText needs at least one locale",
});
export type LocalizedText = z.infer<typeof LocalizedText>;

export function pickLocalized(text: LocalizedText, locale: Locale): string {
  return text[locale] ?? text[DEFAULT_LOCALE] ?? Object.values(text)[0] ?? "";
}

/** 游标分页。 */
export const PageQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type PageQuery = z.infer<typeof PageQuery>;

export function page<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

export const Usage = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cacheReadTokens: z.number().int().nonnegative().default(0),
  cacheWriteTokens: z.number().int().nonnegative().default(0),
  costMicroUsd: z.number().int().nonnegative().default(0),
});
export type Usage = z.infer<typeof Usage>;

export const Ok = z.object({ ok: z.literal(true) });
